import { Link } from '@tanstack/react-router';
import type { CSSProperties, ReactNode } from 'react';
import { Avatar, Body, Heading, Row, Stack } from '@leanin/ui';
import { memberPath } from '../lib/routes';

export type AuthorAttributionProps = {
  name: string;
  photo?: string;
  role?: string;
  company?: string;
  meta?: ReactNode;
  avatarSize?: number;
  eyebrow?: string;
  /**
   * Member ID for the author. When set, the avatar + name link to the
   * member's profile via `memberPath(userId)`. Pass `null` (or omit) for
   * non-member bylines — external contributors, tombstoned authors.
   */
  userId?: string | null;
};

export function AuthorAttribution({
  name,
  photo,
  role,
  company,
  meta,
  avatarSize = 36,
  eyebrow,
  userId,
}: AuthorAttributionProps) {
  const subline = [role, company].filter(Boolean).join(' · ');
  const hasSecondLine = subline.length > 0 || meta != null;
  const href = userId ? memberPath(userId) : null;
  const linkStyle: CSSProperties = { color: 'inherit', textDecoration: 'none' };

  const avatar = <Avatar src={photo} name={name} size={avatarSize} />;
  const avatarNode = href ? (
    <Link
      to={href}
      style={{ ...linkStyle, display: 'inline-flex', flexShrink: 0 }}
      aria-hidden
      tabIndex={-1}
    >
      {avatar}
    </Link>
  ) : (
    avatar
  );

  const nameNode = href ? (
    <Heading level={5}>
      <Link to={href} style={linkStyle}>
        {name}
      </Link>
    </Heading>
  ) : (
    <Heading level={5}>{name}</Heading>
  );

  return (
    <Row gap="sm" align="center">
      {avatarNode}
      <Stack gap="xs">
        {eyebrow ? <Body size="sm" color="mute">{eyebrow}</Body> : null}
        {nameNode}
        {hasSecondLine ? (
          <Body size="sm" color="mute">
            {subline}
            {subline && meta ? ' · ' : ''}
            {meta}
          </Body>
        ) : null}
      </Stack>
    </Row>
  );
}
